import { ITextLibrary } from '../interfaces'
import flatTexts from './flatTexts'

export enum AlertMessageType {
  outOfStock = 'outOfStock',
  AddItem = 'AddItem',
  RemoveItem = 'RemoveItem',
  GetCart = 'GetCart',
  Network = 'Network',
  ReorderPartial = 'ReorderPartial',
  ReorderFailed = 'ReorderFailed',
  SetPaymentMethod = 'SetPaymentMethod',
  SetDeliveryDate = 'SetDeliveryDate',
  CheckoutRuleRouteMissing = 'CheckoutRuleRouteMissing',
  CheckoutRuleSelectionNotOffered = 'CheckoutRuleSelectionNotOffered',
  CheckoutRuleAddonFailed = 'CheckoutRuleAddonFailed',
  CheckoutRuleGenericRejection = 'CheckoutRuleGenericRejection',
  WorkflowStepFailed = 'WorkflowStepFailed',
}

export type AlertMessageTypeKeys = keyof typeof AlertMessageType

export const getAlertErrorMessage = (
  errorType: AlertMessageType,
  texts?: ITextLibrary,
  extraData: number = 0,
): string => {
  const errors = flatTexts(texts?.errors)
  const orders = flatTexts(texts?.orders)
  const paymentMethod = flatTexts(texts?.paymentMethod)
  const deliveryDate = flatTexts(texts?.deliveryDate)
  const workflowStep = flatTexts(texts?.workflowStep)
  const errorIsPlural = extraData > 1
  const stockError = errorIsPlural
    ? errors?.cartOutOfStockErrorPlural
    : errors?.cartOutOfStockErrorSingle
  switch (errorType) {
    case AlertMessageType.AddItem:
      return errors?.cartAddItemError || ''
    case AlertMessageType.RemoveItem:
      return errors?.cartRemoveItemError || ''
    case AlertMessageType.outOfStock:
      return stockError
        ? stockError.replace('{{stock}}', String(extraData))
        : ''
    case AlertMessageType.GetCart:
      return errors?.cartGetItemsError || ''
    case AlertMessageType.Network:
      return errors?.genericNetworkError || ''
    // Reorder succeeded, but the backend dropped lines that are no longer
    // purchasable. `extraData` is how many, substituted the same way
    // `outOfStock` substitutes `{{stock}}`.
    case AlertMessageType.ReorderPartial:
      return orders?.reorderPartialMessage
        ? orders.reorderPartialMessage.replace('{{count}}', String(extraData))
        : ''
    case AlertMessageType.ReorderFailed:
      return orders?.reorderErrorMessage || ''
    /*
     * A picker's write failed, not its read.
     *
     * Separate from `paymentMethod.errorMessage` / `deliveryDate.errorMessage`
     * on purpose: those say the options could not be loaded, and a shopper who
     * is looking at the options and tapped one is owed a different sentence.
     * The read failure is a screen-level message; this is a toast over a
     * screen that still works.
     */
    case AlertMessageType.SetPaymentMethod:
      return paymentMethod?.saveErrorMessage || ''
    case AlertMessageType.SetDeliveryDate:
      return deliveryDate?.saveErrorMessage || ''
    /*
     * A rule blocked the order and this storefront authored no screen for it.
     *
     * The one case where cinnamon has nothing to offer the shopper: the
     * engine named a rule, `IConfig.checkoutRuleRoutes` has no entry for it,
     * and there is no picker to send them to. So the copy points at the
     * conversation rather than at anything in the webview — it is the only
     * place the order can still be finished.
     *
     * Read from `errors` rather than from a per-rule namespace on purpose.
     * The shopper is not on a picker screen and may never have seen one, and
     * naming the rule would describe a step this storefront does not offer.
     */
    case AlertMessageType.CheckoutRuleRouteMissing:
      return errors?.checkoutRuleRouteMissing || ''
    /*
     * COM-1069 reasons that `useCheckoutRuleRecovery` surfaces as toasts.
     *
     * Separate copy per class, falling back to the generic rejection when a
     * tenant has not authored the specific one. All three live under
     * `errors` for the same reason `CheckoutRuleRouteMissing` does: the
     * shopper may not be on a picker screen, and naming a specific rule
     * would describe a step this storefront does not offer.
     *
     * `CheckoutRuleSelectionNotOffered` is the stale-pick case — the
     * backend's `selection_not_offered`. The recovery hook clears the
     * stored selection beside this toast, so the shopper's next pick
     * reaches a fresh options list.
     *
     * `CheckoutRuleAddonFailed` covers `addon_failed` and
     * `addon_invalid_response`. The retry affordance belongs to the
     * storefront's ERP, not to cinnamon, so the toast asks the shopper to
     * try again shortly.
     *
     * `CheckoutRuleGenericRejection` is the fall-through for the rest of
     * COM-1069's reasons — authoring drifts and session timeouts the
     * shopper cannot act on themselves.
     */
    /*
     * ⚠️ **The chains fall back to `checkoutRuleRouteMissing`.** The two
     * specific keys (`checkoutRuleSelectionNotOffered`,
     * `checkoutRuleAddonFailed`) plus `checkoutRuleGenericRejection` ship in
     * all seven `defaultTexts/*.json` files as of this PR, so the fallback
     * never fires on a storefront on fleet copy — but a tenant that cleared
     * any one of them to `''` in their SCS body would otherwise leave the
     * shopper with no toast at all, since `useCheckoutRuleRecovery` only
     * dispatches `addAlert` when the message is truthy and otherwise
     * `return null`s before the no-route branch. Falling back to the one
     * shipped error before this PR (`checkoutRuleRouteMissing`) means the
     * shopper still sees the "we could not complete your order" line rather
     * than nothing. Review of #812 (2026-10-06) — the earlier wording said
     * "three specific keys plus generic", which counted four and is
     * corrected as of COM-1195.
     */
    case AlertMessageType.CheckoutRuleSelectionNotOffered:
      return (
        errors?.checkoutRuleSelectionNotOffered ||
        errors?.checkoutRuleGenericRejection ||
        errors?.checkoutRuleRouteMissing ||
        ''
      )
    case AlertMessageType.CheckoutRuleAddonFailed:
      return (
        errors?.checkoutRuleAddonFailed ||
        errors?.checkoutRuleGenericRejection ||
        errors?.checkoutRuleRouteMissing ||
        ''
      )
    case AlertMessageType.CheckoutRuleGenericRejection:
      return (
        errors?.checkoutRuleGenericRejection ||
        errors?.checkoutRuleRouteMissing ||
        ''
      )
    /*
     * A before-step in the place-order queue failed and authored no handler
     * for it (COM-1296), so the queue stopped and the order was not placed.
     * Falls back to the generic network error for a tenant that cleared the
     * key, so the shopper still learns why nothing happened.
     */
    case AlertMessageType.WorkflowStepFailed:
      return workflowStep?.failed || errors?.genericNetworkError || ''
    default:
      return ''
  }
}

export default getAlertErrorMessage
